// src/core/services/siteSecrets.service.ts
import { readMetadata, removeMetadata, withSiteOperation, writeMetadata } from '@/core/services/storage/siteStorage.service';


/**
 * Defines the shape of the sensitive, non-public data for a site.
 * This data is stored separately and is not included in public site exports.
 */
export interface SiteSecrets {
  cloudinary?: {
    uploadPreset?: string;
    videoUploadPreset?: string;
  };
  imageProviders?: Record<string, Record<string, string | undefined>>;
  publishing?: {
    netlify?: {
      apiToken?: string;
      proxySettings?: {
        useAppProxy?: boolean; // true = use Tauri app proxy, false = use custom proxy URL
        customProxyUrl?: string; // custom proxy URL when not using app proxy
      };
    };
    github?: {
      accessToken?: string;
    };
  };
}

/**
 * Loads the secrets object for a specific site from the database.
 * @param siteId The ID of the site.
 * @returns A promise that resolves to the SiteSecrets object, or an empty object.
 */
export async function loadSiteSecretsFromDb(siteId: string): Promise<SiteSecrets> {
  return (await readMetadata<SiteSecrets>('siteSecrets', siteId)) || {};
}

/**
 * Saves the complete secrets object for a specific site to the database.
 * @param siteId The ID of the site.
 * @param secrets The SiteSecrets object to save.
 */
export async function saveSiteSecretsToDb(siteId: string, secrets: SiteSecrets): Promise<void> {
  await withSiteOperation(siteId, 'secrets-save', async () => {
    await writeMetadata('siteSecrets', siteId, secrets);
  });
}

/**
 * Deletes all secrets for a specific site from the database.
 * This should be called when deleting a site to ensure sensitive data is properly purged.
 * @param siteId The ID of the site whose secrets should be deleted.
 */
export async function deleteSiteSecretsFromDb(siteId: string): Promise<void> {
  await removeMetadata('siteSecrets', siteId);
}
